// Top-level cockpit shell. Owns the RGL responsive grid, the per-tab
// toolbar (edit/lock toggle, add widget, reset, share), and the multi-tab
// strip. Data hooks live above this component on the page; widgets get
// their slice via `widgetProps[id]`.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { WIDGET_REGISTRY, instanceTypeOf } from './widgetRegistry';
import { useCockpitTabs } from './useCockpitTabs';
import WidgetFrame from './WidgetFrame';
import CockpitTabBar from './CockpitTabBar';
import CockpitToolbar from './CockpitToolbar';
import CockpitLibrary from './CockpitLibrary';
import { CockpitProvider } from './cockpitContext.jsx';
import { useInViewport } from './useInViewport';
import { API_BASE_URL } from '../../utils/apiConfig';

const SOFT_INSTANCE_CAP = 15;

// Defers data-hook mounting (and therefore WS subscriptions) until the
// widget scrolls into view. Off-screen widgets render as a "loading" hint
// — no chart canvas, no list virtualizer, no WS chatter. Once visible
// they stay mounted (stickyOnceVisible), so scrolling past doesn't tear
// the chart down.
//
// rootMargin is intentionally generous (1200px) — most viewports are
// 1200-1600px tall, so this pre-mounts roughly one full screen ahead in
// both directions, eliminating the "I scrolled here and nothing's there"
// UX glitch. Memoised so parent re-renders don't churn the observer.
const LazyMountCell = React.memo(function LazyMountCell({ children }) {
  const [ref, visible] = useInViewport({ rootMargin: '1200px' });
  // display:flex+min-height:0 keeps the flex-1 contract that .cockpit-widget-body
  // sets up. Without it, virtualised list children (flex:1, overflow:auto) lose
  // their flex-parent here and grow to content height, breaking scroll.
  return (
    <div
      ref={ref}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      {visible ? children : (
        <div className="cockpit-widget-pending">
          <span className="cockpit-widget-pending-text">Loading…</span>
        </div>
      )}
    </div>
  );
});
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Cockpit.css';


const ResponsiveGrid = WidthProvider(Responsive);

// Single 12-col layout reused across breakpoints — we don't tailor
// per-breakpoint layouts in v1 because RGL would otherwise generate
// surprise "auto" placements on resize. Mobile (<768) falls back to
// stack mode entirely (see CockpitGrid `isMobile` branch).
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS = { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 };

function CockpitGrid({ scope, widgetProps, mobileFallback, extraToolbar }) {
  const tabs = useCockpitTabs(scope);
  const [editing, setEditing] = useState(false);

  // The Default tab is a stable, factory-fresh layout that everyone gets.
  // We never let users edit it — instead, clicking Edit while on Default
  // duplicates it to a new custom tab and edits that copy. That way Default
  // always shows the canonical starting point, and personal tweaks live in
  // user-owned tabs that can be renamed / deleted / shared independently.
  const handleEditToggle = useCallback(() => {
    if (editing) {
      setEditing(false);
      return;
    }
    if (tabs.activeTab?.pinned) {
      // Clone the pinned (Default) tab into a fresh editable copy.
      tabs.addTab({
        name: 'My Layout',
        layout: tabs.activeTab.layout.map((l) => ({ ...l })),
        hidden: [...(tabs.activeTab.hidden || [])],
      });
    }
    setEditing(true);
  }, [editing, tabs]);

  const [libraryOpen, setLibraryOpen] = useState(() => {
    try { return localStorage.getItem('tm.cockpit.library.open') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('tm.cockpit.library.open', String(libraryOpen)); } catch {}
  }, [libraryOpen]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-lock when the user navigates to a pinned (read-only) tab — otherwise
  // they could leave Edit mode "on" and then switch to Default which would
  // render drag handles on a tab that can't actually be modified.
  useEffect(() => {
    if (tabs.activeTab?.pinned && editing) setEditing(false);
  }, [tabs.activeTab?.id, tabs.activeTab?.pinned, editing]);

  // Share-link import: if the URL carries ?preset=ID, fetch it and add as
  // a new tab on first paint. We strip the param afterwards so a browser
  // refresh doesn't re-import (and accidentally pile up duplicate tabs).
  // Ref guard prevents the import from re-firing across React 18 strict-mode
  // double-mount in dev.
  const importedRef = useRef(false);
  useEffect(() => {
    if (importedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const shortId = params.get('preset');
    if (!shortId) return;
    importedRef.current = true;

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/cockpit/presets/${encodeURIComponent(shortId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data?.success || !data.preset) return;
        tabs.addTab({
          name: data.preset.name || 'Shared',
          layout: data.preset.layout,
          hidden: data.preset.hidden || [],
          // widgetSettings is keyed by `i` (layout instance id), and the import
          // preserves the layout verbatim — so the same keys land here on a new
          // tab. Coin / interval / threshold / sort pickers restore exactly as
          // the sharer left them.
          widgetSettings: data.preset.widgetSettings || {},
        });
      } catch {
        // Silent — the preset just doesn't load, user sees their own layout.
      } finally {
        params.delete('preset');
        const cleanQuery = params.toString();
        const newUrl = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLayoutChange = useCallback((layout) => {
    tabs.updateActiveLayout(layout);
  }, [tabs]);

  const { activeTab, visibleInstanceIds, hideWidget } = tabs;

  // Mobile: drop the drag/resize grid (RGL on a tap is painful) and stack
  // every widget vertically in registry order. Pre-built fallback wins if
  // the page supplied one; otherwise we auto-stack from widgetProps so the
  // page doesn't have to duplicate JSX. Tab bar + share still work on
  // mobile (rendered above the stack) so shared links land cleanly.
  if (isMobile) {
    return (
      <CockpitProvider widgetSettings={tabs.widgetSettings} setWidgetSetting={tabs.setWidgetSetting} editing={editing}>
      <div className="cockpit-root cockpit-mobile">
        <div className="cockpit-bar">
          <CockpitTabBar
            tabs={tabs.tabs}
            activeTabId={tabs.activeTabId}
            onActivate={tabs.setActiveTab}
            onClose={tabs.closeTab}
            onRename={tabs.renameTab}
            onAdd={() => tabs.addTab()}
            onDuplicate={tabs.duplicateTab}
            onReorder={tabs.reorderTabs}
          />
        </div>
        {mobileFallback || (
          <div className="cockpit-mobile-stack">
            {tabs.visibleInstanceIds.map((instanceId) => {
              const type = instanceTypeOf(instanceId);
              const def = WIDGET_REGISTRY[type];
              const entry = widgetProps?.[type];
              if (!def || entry == null) return null;
              const rendered = typeof entry === 'function' ? entry(instanceId) : entry;
              const isObjectForm = rendered && typeof rendered === 'object' && !React.isValidElement(rendered) && 'body' in rendered;
              const body = isObjectForm ? rendered.body : rendered;
              // On desktop these live in WidgetFrame's header (coin picker + threshold
              // dropdowns). The RGL stack is dropped on mobile, so we surface them in
              // a compact strip above each cell — otherwise the user can't change coin
              // or volume threshold at all on a phone.
              const headerExtras = isObjectForm ? rendered.headerExtras : null;
              return (
                <div key={instanceId} className={`cockpit-mobile-cell cockpit-mobile-cell-${type}`}>
                  {headerExtras && (
                    <div className="cockpit-mobile-cell-header">{headerExtras}</div>
                  )}
                  {body}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </CockpitProvider>
    );
  }

  return (
    <CockpitProvider widgetSettings={tabs.widgetSettings} setWidgetSetting={tabs.setWidgetSetting} editing={editing}>
    <div className={`cockpit-root ${editing ? 'editing' : ''}`}>
      <div className="cockpit-bar">
        <CockpitTabBar
          tabs={tabs.tabs}
          activeTabId={tabs.activeTabId}
          onActivate={tabs.setActiveTab}
          onClose={tabs.closeTab}
          onRename={tabs.renameTab}
          onAdd={() => tabs.addTab()}
          onDuplicate={tabs.duplicateTab}
          onReorder={tabs.reorderTabs}
        />
        {/* extraToolbar only renders on the pinned Default tab — custom tabs
            are personalised dashboards and shouldn't carry the page-level
            ticker + search up here. */}
        {extraToolbar && activeTab?.pinned && (
          <div className="cockpit-bar-extra">{extraToolbar}</div>
        )}
        <CockpitToolbar
          editing={editing}
          onEditToggle={handleEditToggle}
          onReset={tabs.resetActiveLayout}
          scope={scope}
          activeTab={activeTab}
        />
      </div>

      <div className={`cockpit-main ${editing && libraryOpen ? 'with-library' : ''}`}>
      <div className="cockpit-scroll">
      {/* Re-mount the entire RGL subtree on tab switch via the keyed wrapper.
          Singleton widgets (Chart, Whales, etc.) live under the same React
          `key` across tabs, which would otherwise let React reuse the same
          component instance and leak its useState (timeframe, sort, expanded
          panels…) from tab to tab. Shared data streams stay subscribed via
          refcount overlap during the swap, so the cost is mostly the
          lightweight-charts canvas re-init — acceptable on an explicit user
          action like a tab click. */}
      <div key={activeTab.id} className="cockpit-tab-scope" style={{ height: '100%' }}>
      <ResponsiveGrid
        className="cockpit-grid"
        layouts={{ lg: activeTab.layout, md: activeTab.layout, sm: activeTab.layout, xs: activeTab.layout, xxs: activeTab.layout }}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={30}
        margin={[8, 8]}
        containerPadding={[8, 8]}
        isDraggable={editing}
        isResizable={editing}
        draggableHandle=".cockpit-drag-handle"
        onLayoutChange={handleLayoutChange}
        // useCSSTransforms is true by default; keep it — much smoother drag.
        // compactType:'vertical' is the default; matches Notion/Grafana feel.
      >
        {visibleInstanceIds.map((instanceId) => {
          const type = instanceTypeOf(instanceId);
          const def = WIDGET_REGISTRY[type];
          if (!def) return null;
          const entry = widgetProps?.[type];
          if (entry == null) return null;
          // widgetProps[type] is either a render fn `(instanceId) => body | {body, headerExtras}`
          // (preferred — lets per-instance state live inside the widget) or a
          // static ReactNode/object (singleton legacy).
          const rendered = typeof entry === 'function' ? entry(instanceId) : entry;
          const isObjectForm = rendered && typeof rendered === 'object' && !React.isValidElement(rendered) && 'body' in rendered;
          const body = isObjectForm ? rendered.body : rendered;
          const headerExtras = isObjectForm ? rendered.headerExtras : null;
          return (
            <div key={instanceId}>
              <WidgetFrame
                title={def.title}
                editing={editing}
                pinned={def.pinned}
                onHide={() => hideWidget(instanceId)}
                headerExtras={headerExtras}
              >
                {def.pinned ? body : <LazyMountCell>{body}</LazyMountCell>}
              </WidgetFrame>
            </div>
          );
        })}
      </ResponsiveGrid>
      </div>
      </div>
      {editing && (
        <CockpitLibrary
          availableIds={tabs.availableWidgetIds}
          countsByType={tabs.visibleCountsByType}
          onToggle={tabs.toggleWidget}
          open={libraryOpen}
          onToggleOpen={() => setLibraryOpen((v) => !v)}
        />
      )}
      </div>
    </div>
    </CockpitProvider>
  );
}

export default CockpitGrid;
